import undo from '@/mixins/Customizer/Undo';

export default {
  mixins: [undo],
  data() {
    return {
      message0: '',
      message1: '',
      showModerationMessage0: false,
      showModerationMessage1: false,
    };
  },
  computed: {
    getComponentName() {
      return this.$store.getters['personalization/getPersonalizationComponentName'];
    },
    isMobile() {
      return this.$store.getters['utils/returnScreenSize'].includes('mobile');
    },
    isPersonalizationInvalid() {
      return this.$store.state.transferDesign.isPersonalizationInvalid;
    },
    itemsToPersonalize() {
      return this.$store.getters['personalization/getItemsData'].length;
    },
    locale() {
      return this.$store.state.utils.configuration.locale;
    },
    moderationMessage() {
      const rawText = this.translations.embroideryModerationMessage;
      const textWithLink = rawText.replace("{0}", this.translations.embroideryModerationTermsUrl);
      return `
        <div>
          <div class="embroidery-main-view-label-tooltip-error-icon">!</div>
          <p>${ textWithLink }</p>
        </div>
      `;
    },
    recipe_v2() {
      return this.$store.state.recipe.recipe_v2;
    },
    translations() {
      return this.$store.state.translations.data;
    },
  },
  methods: {
    /**
     * @function handleClickNextButton
     * @description Handle the click event on the "Next" button by submitting to moderation and reporting to analytics
     */
    ctaAction() {
      this.moderationText();
      this.$embroideryDataLayer.logAnalyticsEvent({
        eventLabel: this.$embroideryDataLayer.labels.next
      });
    },

    getActiveMerchandiseRemovePersonalizationComponent() {
      const { swatches } = this.pdm?.merchandising_rules || {};
      let rule = null;
      if (swatches) {
        const { components } = this.recipe_v2;
        rule = Object.values(swatches).find(rule => rule.remove_personalization && components[rule.activate_components?.[0]]);
      }

      return rule ? rule.activate_components[0] : null;
    },

    /**
     * @function imagesLoaded
     * Function passed to imageView component.
     * The event will be called once the images are loaded and will enable
     * the buttons in the modal.
     */
    imagesLoaded(value = true) {
      this.$emit('disableButtons', !value);
    },

    /**
     * @function maxLength
     * This method set default maxlength in the input.
     */
    maxLength() {
      const max_character_length = this.$store.state.utils.configuration
        .personalization.max_character_length;
      const max_character_length_sku = max_character_length[this.pdm.sku];

      return max_character_length_sku ? max_character_length_sku : max_character_length.default;
    },

    moderationMergedText(mergedWord) {
      this.$store.dispatch(
        'personalization/GET_MODERATION_CHECK',
        { locale: this.locale, text: mergedWord.toUpperCase() },
      )
        .then(() => this.nextStep())
        .catch((e) => {
          this.showModerationMessage0 = true;
          this.$emit('blockCtaPrimaryButton', true);
        });
    },

    /**
     * @function moderationText
     * This method validates the inputs to check if the text is allowed.
     * If the text is allowed go to the next step.
     * Otherwise, show a tooltip above the each input.
     */
    moderationText() {
      const moderationMessages = [];
      const promises = [];
      const words = [];

      for (let index = 0; index < this.itemsToPersonalize; index++) {
        const contentToValidate = this[`message${index}`];
        if (contentToValidate === '') continue;

        words.push(contentToValidate);
        moderationMessages.push(`showModerationMessage${ index }`);
        promises.push(
          this.$store.dispatch(
            'personalization/GET_MODERATION_CHECK',
            { locale: this.locale, text: contentToValidate.toUpperCase() },
          )
            .catch(e => console.error(e))
        );
      }

      Promise.all(promises).then(values => {
        let hasTextUnallowed = false;
        for (let [index, value] of values.entries()) {
          if (Number(value) !== 0) {
            this[moderationMessages[index]] = true;
            hasTextUnallowed = true;
          }
        }

        if (!hasTextUnallowed && words.length > 1) {
          this.moderationMergedText(words.join(''));
        } else if (!hasTextUnallowed) {
          this.nextStep();
        } else {
          this.$emit('blockCtaPrimaryButton', true);
        }
      }).catch(error => console.error(error));
    },

    /**
     * @function nextStep
     * This method validate the input and chance the current step.
     */
    nextStep() {
      if (this.recipe_v2.components[this.getComponentName].personalization) {
        this.savePreviousEmbroidery(
          this.recipe_v2.components[this.getComponentName].personalization,
          this.getComponentName
        );
      } else {
        const geometryComponent = this.getActiveMerchandiseRemovePersonalizationComponent();
        const subgroup = this.$store.getters[
          "swatches/returnSwatchSubgroupName"
        ]({
          swatch: this.$store.state.undo?.previousSwatch?.[0]?.swatch,
        });

        if (geometryComponent) {
          this.savePreviousSwatch({ feature: false, geometryComponent });
        } else if (subgroup !== "branding") {
          this.savePreviousEmbroidery([], this.getComponentName);
        }
      }

      if (this.message0 === '' && this.message1 === '') {
        this.$emit('blockCtaPrimaryButton', true);
        return;
      }

      if (this.message0 !== '') {
        this.$store.commit("personalization/SAVE_DATA", {
          text: this.message0.toUpperCase(),
          index: 0
        });
      }

      if (this.message1 !== '') {
        this.$store.commit("personalization/SAVE_DATA", {
          text: this.message1.toUpperCase(),
          index: 1
        });
      }

      this.$store.commit('personalization/NEXT_STEP');
      this.$store.commit('personalization/SET_FONT_SIZE');
      this.$store.commit('personalization/ADD_PERSONALIZATION_COMPONENT', this.getComponentName);
      this.$personalization.removeDisallowedComponents(this.getComponentName);
      this.$store.commit('personalization/CHANGE_VIEW', 'personalization-confirm-view');
      this.$eventBus.$emit('set-component-personalization-modal', 'personalizationConfirmView');
    },

    /**
     * @function validateInputText
     * This method gets the value from the input and validates the text with the correct regex.
     */
    validateInputText(event) {
      const inputId = event.target.dataset.slideId;
      this[`showModerationMessage${ inputId }`] = false;

      const data = this.$store.getters["personalization/getItemsData"][inputId];

      this[`message${ inputId }`] = this.validateRegExp(event.target.value, data)
        .substr(0, this.maxLength(inputId));

      this.$emit('blockCtaPrimaryButton', !this.message0.length && !this.message1.length);
    },

    /**
     * @function validateRegExp
     * This method gets the text and validates the Regex
     */
    validateRegExp(value, data) {
      const personalizationCharsNotAllowed =
        new RegExp(`([^${ data.personalizationCharsAllowed.default }]+)`, 'g');

      return (value || '').replace(personalizationCharsNotAllowed, '');
    },
  },
  beforeCreate() {
    this.$store.commit('personalization/RESET_STEP');
    this.$store.commit('personalization/DELETE_DATA');
    this.$store.commit('ugc/tempData/SET_RECIPE', this.$store.state.recipe.recipe_v2);

    if (this.$store.getters['personalization/isPersonalizationInComponentRecipe']()) {
      this.$store.commit('personalization/CHANGE_VIEW', 'personalization-confirm-view');
      this.$store.commit('personalization/LAST_STEP');
    } else {
      this.$store.commit('personalization/CHANGE_VIEW', 'personalization-main-view');
    }
  },
  created() {
    this.$emit('blockCtaPrimaryButton', true);
    this.$emit('disableButtons', true);
    this.$emit('showIconButton', true);
  },
  destroyed() {
    if (this.isPersonalizationInvalid) {
      this.$store.commit('transferDesign/SET_IS_PERSONALIZATION_INVALID', false);
    }
  }
};
